import React, { useSyncExternalStore } from 'react';
import { Avatar, Box, Divider, Paper, Typography } from '@mui/material';
import {
  Badge as BadgeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import {
  getInvitingAgent,
  subscribeInvitingAgent,
  type ShareInvitingAgent,
} from '../services/shareContext';

/**
 * Standalone card that surfaces the inviting agent's contact details.
 * Used by the "trial period ended" screen so the recipient still has a
 * direct path to reach out for a new link. Mirrors the popover layout
 * in `PublicHeader` (same Avatar / row treatment) for visual consistency.
 *
 * Reads the agent from the shared `shareContext` store via
 * `useSyncExternalStore` so it works in any branch of the SPA without
 * needing the parent to thread the data through props.
 */
export const InvitingAgentCard: React.FC = () => {
  const agent = useSyncExternalStore<ShareInvitingAgent | null>(
    subscribeInvitingAgent,
    getInvitingAgent,
    () => null,
  );
  if (!agent) return null;

  const initials = agentInitials(agent.name, agent.ceaNumber);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2.5,
        p: 2.5,
        maxWidth: 480,
        width: '100%',
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text-strong)',
        border: '1px solid rgba(var(--color-text-rgb), 0.12)',
        borderRadius: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'rgba(var(--color-text-rgb), 0.6)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 1.5,
        }}
      >
        Contact your agent
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Avatar
          src={agent.photo ?? undefined}
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.2)',
            color: 'var(--color-text-strong)',
            fontSize: '1.1rem',
            fontWeight: 700,
            '& img': { objectFit: 'contain', objectPosition: 'center top' },
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {agent.name || 'Your Huttons Agent'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}
          >
            Huttons Asia Pte Ltd
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      <AgentDetailRow
        icon={<BadgeIcon fontSize="small" />}
        label="CEA Number"
        value={agent.ceaNumber}
      />
      <AgentDetailRow
        icon={<PhoneIcon fontSize="small" />}
        label="Phone"
        value={agent.mobile}
        href={agent.mobile ? `tel:${agent.mobile}` : undefined}
      />
      <AgentDetailRow
        icon={<EmailIcon fontSize="small" />}
        label="Email"
        value={agent.email}
        href={agent.email ? `mailto:${agent.email}` : undefined}
      />
    </Paper>
  );
};

const agentInitials = (name: string | null | undefined, fallback: string): string => {
  const source = (name && name.trim().length > 0 ? name : fallback).trim();
  if (!source) return '?';
  return source
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/** Single labelled row inside the card. Mirrors `AgentDetailRow` in `PublicHeader`. */
const AgentDetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null;
  href?: string;
}> = ({ icon, label, value, href }) => {
  const display = value && value.trim().length > 0 ? value : '—';
  const isLink = Boolean(href && value && value.trim().length > 0);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75 }}>
      <Box
        sx={{
          color: 'var(--color-accent)',
          display: 'inline-flex',
          alignItems: 'center',
          mt: '2px',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(var(--color-text-rgb), 0.6)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        {isLink ? (
          <Typography
            component="a"
            href={href}
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-accent)',
              textDecoration: 'none',
              wordBreak: 'break-all',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {display}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--color-text-strong)',
              wordBreak: 'break-all',
            }}
          >
            {display}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InvitingAgentCard;
